import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Play, Pause, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { sendPlaylistEvent } from '../../utils/playlistEvents';

export default function AudioBlock({ block, playlist, blockIndex, onComplete, persistentCTA }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [watchStartTime] = useState(Date.now());
  const [duration, setDuration] = useState(0);
  
  const audioUrl = block.props?.audio_url || block.props?.mp3_url || '';
  const title = block.props?.title || 'Audio';

  useEffect(() => {
    sendPlaylistEvent(playlist.id, 'block_start', {
      block_id: block.id,
      block_index: blockIndex,
      block_type: 'audio'
    });

    // Auto-play
    if (audioRef.current && audioUrl) {
      audioRef.current.play().catch(err => {
        console.log('Audio autoplay blocked:', err);
      });
    }

    // Fallback: if audio doesn't load or has no duration, auto-advance after 10s
    const fallbackTimer = setTimeout(() => {
      if (!audioRef.current || audioRef.current.paused || audioRef.current.duration === 0) {
        sendPlaylistEvent(playlist.id, 'block_complete', {
          block_id: block.id,
          block_index: blockIndex,
          watch_ms: Date.now() - watchStartTime
        });
        if (onComplete) onComplete();
      }
    }, 10000);

    return () => clearTimeout(fallbackTimer);
  }, [block.id, blockIndex, onComplete, playlist.id, watchStartTime, audioUrl]);

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const percent = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(percent);
    }
  };

  const handleEnded = () => {
    sendPlaylistEvent(playlist.id, 'block_complete', {
      block_id: block.id,
      block_index: blockIndex,
      watch_ms: Date.now() - watchStartTime
    });
    if (onComplete) onComplete();
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleCTAClick = useCallback((e) => {
    e.stopPropagation();
    sendPlaylistEvent(playlist.id, 'cta_click', {
      block_id: block.id,
      block_index: blockIndex,
      url: persistentCTA?.url,
      label: persistentCTA?.label
    });
    if (persistentCTA?.url) {
      window.open(persistentCTA.url, '_blank', 'noopener,noreferrer');
    }
  }, [playlist.id, block.id, blockIndex, persistentCTA]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="relative w-full h-full bg-gradient-to-br from-purple-900 to-indigo-900 flex flex-col items-center justify-center p-8">
      <audio
        ref={audioRef}
        src={audioUrl}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      {/* Audio visualizer mockup */}
      <div className="w-full max-w-md mb-8">
        <h3 className="text-white text-2xl font-bold mb-4 text-center">{title}</h3>
        
        {/* Simple waveform visualization */}
        <div className="flex items-center justify-center gap-1 h-24 mb-6">
          {[...Array(40)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-full transition-all"
              style={{
                width: '4px',
                height: `${20 + Math.random() * 60}%`,
                opacity: i < (progress / 100) * 40 ? 1 : 0.3
              }}
            />
          ))}
        </div>

        {/* Progress bar */}
        <div className="w-full bg-white/20 rounded-full h-2 mb-4">
          <div
            className="bg-white h-2 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Time */}
        <div className="flex justify-between text-white text-sm mb-6">
          <span>{formatTime(audioRef.current?.currentTime || 0)}</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Play/Pause button */}
        <div className="flex justify-center">
          <Button
            onClick={togglePlayPause}
            size="lg"
            className="rounded-full w-16 h-16 bg-white text-black hover:bg-gray-200"
          >
            {isPlaying ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8" />}
          </Button>
        </div>
      </div>

      {/* Persistent CTA */}
      {persistentCTA && (
        <div 
          className="absolute bottom-0 left-0 right-0 p-4"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
        >
          <Button
            onClick={handleCTAClick}
            className="w-full bg-white text-black hover:bg-gray-200 font-semibold"
            size="lg"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {persistentCTA.label}
          </Button>
        </div>
      )}
    </div>
  );
}
